'use client';

import { AnimatePresence } from 'framer-motion';
import type { ButtonHTMLAttributes, MouseEvent, Ref } from 'react';

import { Icon, type Icons } from '../icon';
import { cn } from '../lib/utils';

import { useButtonGroup } from '../button/group-context';
import { LoadingBar } from '../button/loading-bar';
import {
  type Size,
  type Variant,
  contentLayer,
  disabledStyles,
  iconSize,
  interactiveBase,
  loadingStyles,
  variantStyles,
} from '../button/tokens';
import { useAsyncClick } from '../button/use-async-click';

const sizeStyles: Record<Size, string> = {
  sm: 'size-7 rounded-lg',
  md: 'size-8 rounded-xl',
  lg: 'size-10 rounded-2xl',
  xl: 'size-14 rounded-3xl',
};

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  /** Icon name from the design-system icon set. */
  icon: Icons;
  /** Required — passed to `aria-label`. Icon-only without label is an a11y violation. */
  label: string;
  /** Visual style. Inherited from a parent `ButtonGroup` if present.
   *  @default 'ghost' */
  variant?: Variant;
  /** Square size token. Inherited from a parent `ButtonGroup` if present.
   *  @default 'md' */
  size?: Size;
  /** Controls the loading state. **Uncontrolled** when omitted: the button
   *  auto-detects a Promise returned from `onClick` and shows the loading
   *  bar until it resolves. **Controlled** when provided: your value wins
   *  and the Promise auto-detection is ignored — pass `isLoading={false}`
   *  to suppress the bar for incidental Promises (router navigate,
   *  fire-and-forget telemetry), or `isLoading={mutation.isPending}` to
   *  drive it from react-query. Subsequent clicks are still blocked
   *  while the Promise is pending regardless of this prop. */
  isLoading?: boolean;
  /** Sync or async click handler. If it returns a Promise and `isLoading`
   *  is not provided, the button shows a loading bar until the Promise
   *  resolves. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => unknown;
  /** Forwarded to the underlying `<button>`. */
  ref?: Ref<HTMLButtonElement>;
};

export const IconButton = ({
  icon,
  label,
  variant: variantProp,
  size: sizeProp,
  isLoading,
  onClick,
  className,
  type = 'button',
  disabled = false,
  ref,
  ...props
}: Props) => {
  const group = useButtonGroup();
  const variant = group?.variant ?? variantProp ?? 'ghost';
  const size = group?.size ?? sizeProp ?? 'md';

  const { handleClick, isPending } = useAsyncClick({
    onClick,
    // `=== true` is load-bearing: `isLoading` is tri-state and we only want
    // to block clicks when explicitly controlled to `true`. Simplifying to
    // `disabled || isLoading` would break the `isLoading={false}` opt-out.
    isBlocked: disabled || isLoading === true,
  });
  // Controlled when `isLoading` is provided (the explicit value wins);
  // uncontrolled when omitted (the Promise return from `onClick` drives
  // the bar). Mirrors the React controlled/uncontrolled pattern.
  const showSpinner = isLoading ?? isPending;

  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      disabled={disabled}
      aria-disabled={showSpinner ? true : undefined}
      aria-busy={showSpinner ? true : undefined}
      onClick={handleClick}
      className={cn(
        interactiveBase,
        sizeStyles[size],
        variantStyles[variant],
        showSpinner ? loadingStyles : disabledStyles,
        className,
      )}
      {...props}
    >
      <AnimatePresence>{showSpinner && <LoadingBar variant={variant} />}</AnimatePresence>
      <span data-slot="content" className={contentLayer}>
        {/* `font-medium` overrides any inherited bold so the icon-font stays clean. */}
        <Icon name={icon} className={cn('font-medium', iconSize[size])} />
      </span>
    </button>
  );
};

export type { Props as IconButtonProps };
